import { useApi } from '@omnipresentgroup/api';
import type { GetInvoiceReportsResponseAPI } from '@omnipresentgroup/types';
import { useQuery } from '@tanstack/react-query';

import { API_BASE, API_PATH } from '../constants';
import { transformGetInvoiceReportsResponseFromAPI } from '../mappers';

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

type QueryParameters = {
  companyId: string;
  page: number;
  size: number;
  sort: string;
  order: SortOrder;
  enabled?: boolean;
};

export const getInvoiceReportsKey = ({
  companyId,
  page,
  size,
  sort,
  order,
  sortParam,
}: Omit<QueryParameters, 'enabled'> & {
  sortParam: string;
}) => [API_PATH, companyId, page, size, sort, order, sortParam];

export const useInvoiceReports = ({
  companyId,
  page,
  size,
  sort,
  order,
  enabled,
}: QueryParameters) => {
  const sortParameter = sort.replace(
    /[A-Z]/g,
    (letter) => `_${letter.toLowerCase()}`,
  );

  const { GET } = useApi();
  const query = useQuery({
    queryKey: getInvoiceReportsKey({
      companyId,
      page,
      size,
      sort,
      order,
      sortParam: sortParameter,
    }),
    queryFn: () =>
      GET<GetInvoiceReportsResponseAPI>(
        `${API_BASE}/${API_PATH}/${companyId}`,
        {},
        {
          page: `${page}`,
          size: `${size}`,
          sort: `${sortParameter}`,
          order: `${order}`,
        },
      ),
    select: (data) => ({
      ...data,
      data: transformGetInvoiceReportsResponseFromAPI(
        data as GetInvoiceReportsResponseAPI,
      ).data,
    }),
    enabled: enabled || !!companyId,
  });

  return query;
};
